import {
  ENERGY_COST, SimState, XP_REWARDS,
  MUSHROOM_GROW_TIME, MUSHROOM_REGROW_DELAY,
  BERRY_GROW_TIME, BERRY_REGROW_DELAY, SAPLING_GROW_TIME,
  TREE_SAPLING_GROW_TIME,
} from './types';
import { EnergySystem } from './EnergySystem';
import { ProgressionSystem } from './ProgressionSystem';
import { PlotSystem } from './PlotSystem';
import { EventBus } from './events';

/**
 * Tree / bush / boulder clearing + forage growth.
 *
 * Lifecycle:
 *   tree-oak
 *     ↓ chop (axe)
 *   tree-oak-stump + fallen log on the same tile, +3 wood, +1-2 acorns
 *     ↓ chop the fallen log (axe)        → +2 wood, log gone; mushrooms keep growing on the stump
 *     OR leave the log for faster, higher-yield mushroom growth
 *     ↓ chop the bare stump (axe)        → +1 wood, tile cleared
 *
 * Mushrooms on a stump tile grow stage 0→1→2 over time. A FALLEN log on the
 * same tile boosts both growth rate (1.6×) and harvest yield (2-3 vs 1-2).
 * Bushes bear berries the same way; harvesting resets to stage 0 with a delay.
 * Berries can be replanted on a cleared grass tile → bush-sapling → bush.
 */

/** Growth-speed multiplier when a felled trunk lies on the same tile. */
const MUSH_FALLEN_BOOST = 1.6;

export const TreeSystem = {
  canChop(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.thing === 'tree-oak') return EnergySystem.has(state, ENERGY_COST.chopTree);
    if (t.thing === 'tree-oak-stump') {
      // Cheaper to chip a downed log than to dig out a stump.
      const cost = t.fallen ? ENERGY_COST.chopTree : ENERGY_COST.clearStump;
      return EnergySystem.has(state, cost);
    }
    if (t.thing === 'rock-boulder') return EnergySystem.has(state, ENERGY_COST.chopTree);
    if (t.thing === 'bush') return EnergySystem.has(state, ENERGY_COST.clearBush);
    if (t.thing === 'bush-sapling') return EnergySystem.has(state, ENERGY_COST.clearBush);
    if (t.thing === 'tall-grass') return EnergySystem.has(state, ENERGY_COST.clearBush);
    return false;
  },

  chop(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (!t.thing) {
      bus.emit({ type: 'blocked', reason: 'Nothing to chop here' });
      return false;
    }
    if (t.thing === 'tree-oak') {
      if (!EnergySystem.spend(state, ENERGY_COST.chopTree)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      t.thing = 'tree-oak-stump';
      // The trunk lands alongside the stump and stays until the player
      // chops it for lumber. Until then it accelerates mushroom growth.
      t.fallen = true;
      t.mushStage = 0;
      t.mushTimer = MUSHROOM_REGROW_DELAY;
      state.res.wood += 3;
      const acorns = 1 + Math.floor(Math.random() * 2);
      state.res.treeSeeds = (state.res.treeSeeds ?? 0) + acorns;
      ProgressionSystem.addXp(state, XP_REWARDS.chopTree, bus);
      bus.emit({ type: 'hint', text: `Tree felled — +3 wood, +${acorns} acorn${acorns === 1 ? '' : 's'}. Leave the log for richer mushrooms, or chop it for +2 wood.` });
      bus.emit({ type: 'chopped', col: c, row: r, wood: 3, acorns });
      return true;
    }
    if (t.thing === 'tree-oak-stump') {
      // First strike on a stump tile with a fallen log: clears the log only.
      if (t.fallen) {
        if (!EnergySystem.spend(state, ENERGY_COST.chopTree)) {
          bus.emit({ type: 'blocked', reason: 'Not enough energy' });
          return false;
        }
        t.fallen = false;
        state.res.wood += 2;
        ProgressionSystem.addXp(state, XP_REWARDS.chopTree, bus);
        bus.emit({ type: 'hint', text: '+2 wood — log cleared. Mushrooms still growing on the stump.' });
        bus.emit({ type: 'chopped', col: c, row: r, wood: 2 });
        return true;
      }
      if (!EnergySystem.spend(state, ENERGY_COST.clearStump)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      // Salvage any mushrooms growing on the stump so clearing doesn't
      // silently destroy them. Mature (stage 2) drops the full yield;
      // half-grown (stage 1) drops 1; bare (stage 0) drops nothing.
      const ms = t.mushStage ?? 0;
      let salvaged = 0;
      if (ms >= 2) salvaged = 1 + Math.floor(Math.random() * 2); // 1-2
      else if (ms >= 1) salvaged = 1;
      if (salvaged > 0) state.res.mushrooms = (state.res.mushrooms ?? 0) + salvaged;
      t.thing = null;
      delete t.mushStage;
      delete t.mushTimer;
      delete t.fallen;
      state.res.wood += 1;
      ProgressionSystem.addXp(state, XP_REWARDS.clearStump, bus);
      const hint = salvaged > 0
        ? `+1 wood, +${salvaged} mushroom${salvaged === 1 ? '' : 's'} — tile cleared.`
        : '+1 wood — tile cleared, you can hoe it now.';
      bus.emit({ type: 'hint', text: hint });
      bus.emit({ type: 'chopped', col: c, row: r, wood: 1, mushrooms: salvaged > 0 ? salvaged : undefined });
      return true;
    }
    if (t.thing === 'rock-boulder') {
      // Boulders need the pickaxe — axe just bounces. Drops 2-3 stones.
      if (state.tool !== 'pickaxe') {
        bus.emit({ type: 'hint', text: 'Use the PICKAXE on boulders.' });
        return false;
      }
      if (!EnergySystem.spend(state, ENERGY_COST.chopTree)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      const yield_ = 2 + Math.floor(Math.random() * 2);
      state.res.stone = (state.res.stone ?? 0) + yield_;
      t.thing = null;
      ProgressionSystem.addXp(state, XP_REWARDS.breakBoulder, bus);
      bus.emit({ type: 'hint', text: `+${yield_} stone — useful for building foundations.` });
      bus.emit({ type: 'chopped', col: c, row: r, stone: yield_ });
      return true;
    }
    if (t.thing === 'bush' || t.thing === 'bush-sapling' || t.thing === 'tall-grass') {
      if (!EnergySystem.spend(state, ENERGY_COST.clearBush)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      // Salvage berries from a bush being cleared. Same rules as the
      // mushroom salvage above — ripe (stage 2) drops the full yield,
      // green (stage 1) drops 1, bare (stage 0) drops nothing.
      const bs = t.berryStage ?? 0;
      let berriesSalvaged = 0;
      if (t.thing === 'bush') {
        if (bs >= 2) berriesSalvaged = 2 + Math.floor(Math.random() * 3); // 2-4
        else if (bs >= 1) berriesSalvaged = 1;
      }
      if (berriesSalvaged > 0) {
        state.res.berries = (state.res.berries ?? 0) + berriesSalvaged;
        bus.emit({ type: 'hint', text: `+${berriesSalvaged} berries — bush cleared.` });
      }
      t.thing = null;
      delete t.berryStage;
      delete t.berryTimer;
      delete t.saplingTimer;
      ProgressionSystem.addXp(state, XP_REWARDS.clearBush, bus);
      bus.emit({ type: 'chopped', col: c, row: r, berries: berriesSalvaged > 0 ? berriesSalvaged : undefined });
      return true;
    }
    if (t.thing === 'tree-oak-sapling') {
      if (!EnergySystem.spend(state, ENERGY_COST.clearBush)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      t.thing = null;
      delete t.treeSaplingTimer;
      // Return the acorn so accidental felling isn't a hard loss.
      state.res.treeSeeds = (state.res.treeSeeds ?? 0) + 1;
      ProgressionSystem.addXp(state, XP_REWARDS.clearSapling, bus);
      bus.emit({ type: 'hint', text: 'Sapling cleared — acorn returned to your bag.' });
      bus.emit({ type: 'chopped', col: c, row: r, acorns: 1 });
      return true;
    }
    return false;
  },

  /** True when there's something ripe to forage on this tile. */
  canForage(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.thing === 'tree-oak-stump' && (t.mushStage ?? 0) >= 2) return EnergySystem.has(state, ENERGY_COST.forage);
    if (t.thing === 'bush' && (t.berryStage ?? 0) >= 2) return EnergySystem.has(state, ENERGY_COST.forage);
    return false;
  },

  /** Pluck mushrooms / berries off the prop. Resets growth so it regrows. */
  forage(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.thing === 'tree-oak-stump' && (t.mushStage ?? 0) >= 2) {
      if (!EnergySystem.spend(state, ENERGY_COST.forage)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      // Fallen log = richer colony.
      const yld = t.fallen
        ? 2 + Math.floor(Math.random() * 2)   // 2-3
        : 1 + Math.floor(Math.random() * 2);  // 1-2
      state.res.mushrooms = (state.res.mushrooms ?? 0) + yld;
      t.mushStage = 0;
      t.mushTimer = MUSHROOM_REGROW_DELAY;
      ProgressionSystem.addXp(state, XP_REWARDS.forageMushroom, bus);
      bus.emit({ type: 'hint', text: `+${yld} mushroom${yld === 1 ? '' : 's'} — ${t.fallen ? 'the log fruits again soon.' : 'the stump will fruit again.'}` });
      bus.emit({ type: 'foraged', col: c, row: r, kind: 'mushroom', amount: yld });
      return true;
    }
    if (t.thing === 'bush' && (t.berryStage ?? 0) >= 2) {
      if (!EnergySystem.spend(state, ENERGY_COST.forage)) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy' });
        return false;
      }
      const yld = 2 + Math.floor(Math.random() * 3);
      state.res.berries = (state.res.berries ?? 0) + yld;
      t.berryStage = 0;
      t.berryTimer = BERRY_REGROW_DELAY;
      ProgressionSystem.addXp(state, XP_REWARDS.forageBerry, bus);
      bus.emit({ type: 'hint', text: `+${yld} berries — the bush will fruit again.` });
      bus.emit({ type: 'foraged', col: c, row: r, kind: 'berry', amount: yld });
      return true;
    }
    return false;
  },

  /** Plant a berry on a cleared grass tile → grows into a sapling, then a bush. */
  canPlantBerry(state: SimState, c: number, r: number): boolean {
    if ((state.res.berries ?? 0) <= 0) return false;
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld || t.thing || t.crop) return false;
    if (t.surf !== 'grass') return false;
    return EnergySystem.has(state, ENERGY_COST.plantBerry);
  },

  plantBerry(state: SimState, c: number, r: number, bus: EventBus): boolean {
    if ((state.res.berries ?? 0) <= 0) {
      bus.emit({ type: 'blocked', reason: 'No berries to plant.' });
      return false;
    }
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld || t.thing || t.crop) {
      bus.emit({ type: 'blocked', reason: 'Clear this tile first.' });
      return false;
    }
    if (t.surf !== 'grass') {
      bus.emit({ type: 'blocked', reason: 'Berries grow only on grass.' });
      return false;
    }
    if (!EnergySystem.spend(state, ENERGY_COST.plantBerry)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    state.res.berries = (state.res.berries ?? 0) - 1;
    t.thing = 'bush-sapling';
    t.saplingTimer = SAPLING_GROW_TIME;
    ProgressionSystem.addXp(state, XP_REWARDS.plantBerry, bus);
    bus.emit({ type: 'hint', text: 'Planted a berry — a bush will sprout shortly.' });
    bus.emit({ type: 'berryPlanted', col: c, row: r });
    return true;
  },

  /** Plant an acorn on a cleared grass tile → tree-oak-sapling → tree-oak. */
  canPlantAcorn(state: SimState, c: number, r: number): boolean {
    if ((state.res.treeSeeds ?? 0) <= 0) return false;
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld || t.thing || t.crop) return false;
    if (t.surf !== 'grass') return false;
    return EnergySystem.has(state, ENERGY_COST.plantBerry);
  },

  plantAcorn(state: SimState, c: number, r: number, bus: EventBus): boolean {
    if ((state.res.treeSeeds ?? 0) <= 0) {
      bus.emit({ type: 'blocked', reason: 'No acorns to plant — chop a tree first.' });
      return false;
    }
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld || t.thing || t.crop) {
      bus.emit({ type: 'blocked', reason: 'Clear this tile first.' });
      return false;
    }
    if (t.surf !== 'grass') {
      bus.emit({ type: 'blocked', reason: 'Acorns root only in grass.' });
      return false;
    }
    if (!EnergySystem.spend(state, ENERGY_COST.plantBerry)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    state.res.treeSeeds = (state.res.treeSeeds ?? 0) - 1;
    t.thing = 'tree-oak-sapling';
    t.treeSaplingTimer = TREE_SAPLING_GROW_TIME;
    ProgressionSystem.addXp(state, XP_REWARDS.plantBerry, bus);
    bus.emit({ type: 'hint', text: 'Planted an acorn — a young oak will take a while to grow.' });
    bus.emit({ type: 'acornPlanted', col: c, row: r });
    return true;
  },

  /** Per-frame growth: stumps fruit mushrooms, bushes fruit berries, saplings mature. */
  tick(state: SimState, dt: number, bus: EventBus) {
    PlotSystem.forEachTile(state, (t, c, r) => {
      if (t.thing === 'tree-oak-stump') {
        if (t.mushStage === undefined) { t.mushStage = 0; t.mushTimer = MUSHROOM_REGROW_DELAY; }
        if (t.mushStage < 2) {
          const speed = t.fallen ? MUSH_FALLEN_BOOST : 1.0;
          t.mushTimer = (t.mushTimer ?? MUSHROOM_GROW_TIME) - dt * speed;
          if (t.mushTimer <= 0) {
            t.mushStage++;
            t.mushTimer = MUSHROOM_GROW_TIME;
            bus.emit({ type: 'mushroomGrew', col: c, row: r, stage: t.mushStage });
          }
        }
      }
      if (t.thing === 'bush') {
        if (t.berryStage === undefined) { t.berryStage = 0; t.berryTimer = BERRY_REGROW_DELAY; }
        if (t.berryStage < 2) {
          t.berryTimer = (t.berryTimer ?? BERRY_GROW_TIME) - dt;
          if (t.berryTimer <= 0) {
            t.berryStage++;
            t.berryTimer = BERRY_GROW_TIME;
            bus.emit({ type: 'berryGrew', col: c, row: r, stage: t.berryStage });
          }
        }
      }
      if (t.thing === 'bush-sapling') {
        t.saplingTimer = (t.saplingTimer ?? SAPLING_GROW_TIME) - dt;
        if (t.saplingTimer <= 0) {
          t.thing = 'bush';
          delete t.saplingTimer;
          t.berryStage = 0;
          t.berryTimer = BERRY_REGROW_DELAY;
          bus.emit({ type: 'saplingMatured', col: c, row: r });
        }
      }
      if (t.thing === 'tree-oak-sapling') {
        t.treeSaplingTimer = (t.treeSaplingTimer ?? TREE_SAPLING_GROW_TIME) - dt;
        if (t.treeSaplingTimer <= 0) {
          t.thing = 'tree-oak';
          delete t.treeSaplingTimer;
          bus.emit({ type: 'treeSaplingMatured', col: c, row: r });
        }
      }
    });
  },
};
